import { useState } from "react";
import { Alert, Box, Grid, IconButton, Typography } from "@mui/material";
import { ArrowDownward, ArrowUpward, DeleteOutlineOutlined } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import ButtonLoading from "../Button/ButtonLoading";
import { Dropzone } from "../Dropzone/Dropzone";
import ValidatorForm from "../Validator/ValidatorForm";
import ValidatorTextField from "../Validator/ValidatorTextField";
import LegalbirdTipTapEditorProvider from "../LbEditor/LegalbirdTipTapEditorProvider";
import LegalbirdTipTapEditor from "../LbEditor/LegalbirdTipTapEditor";
import useForm from "../../hooks/useForm";
import {
  createReleaseUpdateMessage,
  deleteReleaseUpdateMediaObject,
  fetchReleaseUpdateMediaObjects,
  RELEASE_UPDATE_MAX_IMAGE_COUNT,
  RELEASE_UPDATE_MAX_IMAGE_SIZE,
  updateReleaseUpdateMessage,
  uploadReleaseUpdateImage,
} from "../../services/releaseUpdateService";
import { ReleaseUpdate, ReleaseUpdateMessageInput, ReleaseUpdatePublication } from "../../types/ReleaseUpdate";
import releaseUpdateStyles from "./releaseUpdatesStyles";
import { MediaObject } from "../../types/MediaObject";
import { formValue } from "../../services/formServiceFunctions";
import { requiredFieldDefault, tipTapEditorDefault } from "../../services/validationRules";
import { UseForm } from "../../types/UseForm";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import _ from "lodash";

type ReleaseUpdateMessageFormProps = {
  publication: ReleaseUpdatePublication;
  message: ReleaseUpdate | null;
  isPublished: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

export default function ReleaseUpdateMessageForm({
  publication,
  message,
  isPublished,
  onClose,
  onSaved,
}: ReleaseUpdateMessageFormProps) {
  const [isUploading, setIsUploading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const initialValues = {
    productArea: message?.productArea ?? "",
    topic: message?.topic ?? "",
    headline: message?.headline ?? "",
    content: message?.content ?? "",
    mediaObjectIds: message?.mediaObjectIds ?? [],
  };

  const handleSave = async ({ values }: { values: UseForm["values"] }) => {
    try {
      const input: ReleaseUpdateMessageInput = {
        productArea: values.productArea.trim() || null,
        topic: values.topic.trim() || null,
        headline: values.headline.trim(),
        content: values.content,
        mediaObjectIds: values.mediaObjectIds,
      };
      if (message) {
        await updateReleaseUpdateMessage(publication.id, message, input, currentUser["@id"]);
      } else {
        await createReleaseUpdateMessage(publication, input, currentUser["@id"]);
      }
      await onSaved();
      onClose();
    } catch (error) {
      const messageText = error instanceof Error ? error.message : "Die Meldung konnte nicht gespeichert werden.";
      enqueueSnackbar(messageText, { variant: "error" });
    }
  };

  const { values, errors, handleChange, handleBlur, handleSubmit, registerValidators, isLoading } = useForm({
    initialValues,
    onSubmit: handleSave,
    identifier: message?.id ?? "new",
    enableUnsavedChangesWarning: true
  });

  const handleClose = () => {
    if (!_.isEqual(initialValues, values) && !confirm("Bist du dir sicher, dass du das Formular verlassen möchtest? Ungespeicherte Änderungen gehen verloren.")) {
      return;
    }

    onClose();
  }

  const mediaObjectIds = formValue(values, "mediaObjectIds") as number[];
  const { data: mediaObjects = [] } = useQuery({
    queryKey: ["releaseUpdateFormMediaObjects", mediaObjectIds],
    queryFn: () => fetchReleaseUpdateMediaObjects(mediaObjectIds),
  });

  const handleUpload = async (files: File[]) => {
    if (mediaObjectIds.length + files.length > RELEASE_UPDATE_MAX_IMAGE_COUNT) {
      enqueueSnackbar(`Pro Meldung sind maximal ${RELEASE_UPDATE_MAX_IMAGE_COUNT} Bilder erlaubt.`, {
        variant: "error",
      });
      return;
    }

    setIsUploading(true);
    try {
      const uploadedMediaObjects: MediaObject[] = [];
      for (const file of files) {
        const uploadedMediaObject = await uploadReleaseUpdateImage(file);
        uploadedMediaObjects.push(uploadedMediaObject);
        handleChange({
          target: {
            name: "mediaObjectIds",
            value: [...mediaObjectIds, ...uploadedMediaObjects.map((mediaObject) => mediaObject.id)],
          },
        });
      }
    } catch {
      enqueueSnackbar("Mindestens ein Bild konnte nicht hochgeladen werden.", { variant: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  const moveImage = (index: number, offset: number) => {
    const nextIds = [...mediaObjectIds];
    const targetIndex = index + offset;
    [nextIds[index], nextIds[targetIndex]] = [nextIds[targetIndex], nextIds[index]];
    handleChange({ target: { name: "mediaObjectIds", value: nextIds } });
  };

  const removeImage = async (mediaObjectId: number) => {
    if (!message?.mediaObjectIds.includes(mediaObjectId)) {
      try {
        await deleteReleaseUpdateMediaObject(mediaObjectId);
      } catch {
        enqueueSnackbar("Das Bild konnte nicht gelöscht werden.", { variant: "error" });
        return;
      }
    }

    handleChange({
      target: {
        name: "mediaObjectIds",
        value: mediaObjectIds.filter((currentId) => currentId !== mediaObjectId),
      },
    });
  };

  return (
    <LegalbirdIoModal
      open
      handleClose={handleClose}
      title={message ? "Meldung bearbeiten" : "Neue Meldung"}
      fullWidth
      maxWidth="md"
      submitButton={
        <ButtonLoading variant="contained" onClick={handleSubmit} isLoading={isLoading} disabled={isUploading}>
          {isPublished ? "Änderungen speichern" : "Meldung speichern"}
        </ButtonLoading>
      }
    >
      <ValidatorForm onSubmit={handleSubmit}>
        {isPublished && (
          <Alert severity="info" sx={{ marginBottom: "1rem" }}>
            Die Korrektur wird sofort sichtbar. Bestehende Lesebestätigungen bleiben erhalten.
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ValidatorTextField
              name="productArea"
              label="Produktbereich"
              value={formValue(values, "productArea")}
              onChange={handleChange}
              fullWidth
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ValidatorTextField
              name="topic"
              label="Thema"
              value={formValue(values, "topic")}
              onChange={handleChange}
              fullWidth
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid size={12}>
            <ValidatorTextField
              required
              name="headline"
              label="Überschrift"
              value={formValue(values, "headline")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              validators={requiredFieldDefault}
              error={!!errors.headline}
              helperText={errors.headline}
              fullWidth
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h5" sx={{ marginBottom: "0.5rem" }}>
              Text
            </Typography>
            <LegalbirdTipTapEditorProvider
              initialContent={initialValues.content}
              placeholder="Text der Meldung"
              onUpdate={({ editor }) => {
                handleChange({ target: { name: "content", value: editor.getHTML() } });
              }}
            >
              <LegalbirdTipTapEditor
                name="content"
                registerValidators={registerValidators}
                validators={tipTapEditorDefault}
                error={!!errors.content}
                helperText={errors.content}
                editorSx={releaseUpdateStyles.editorStyle}
              />
            </LegalbirdTipTapEditorProvider>
          </Grid>
          <Grid size={12}>
            <Typography variant="h5" sx={{ marginBottom: "0.5rem" }}>
              Bilder ({mediaObjectIds.length}/{RELEASE_UPDATE_MAX_IMAGE_COUNT})
            </Typography>
            {mediaObjectIds.map((mediaObjectId, index) => {
              const mediaObject = mediaObjects.find((candidate) => candidate.id === mediaObjectId);
              return (
                <Grid container spacing={1} key={mediaObjectId} sx={releaseUpdateStyles.formImageRow}>
                  <Grid>
                    {mediaObject && (
                      <Box component="img" src={mediaObject.displayUrl} sx={releaseUpdateStyles.formImage} />
                    )}
                  </Grid>
                  <Grid size="grow">
                    <Typography>{mediaObject?.originalName ?? `Bild ${index + 1}`}</Typography>
                  </Grid>
                  <Grid>
                    <IconButton
                      title="Bild nach oben"
                      disabled={index === 0}
                      onClick={() => moveImage(index, -1)}
                    >
                      <ArrowUpward />
                    </IconButton>
                    <IconButton
                      title="Bild nach unten"
                      disabled={index === mediaObjectIds.length - 1}
                      onClick={() => moveImage(index, 1)}
                    >
                      <ArrowDownward />
                    </IconButton>
                    <IconButton title="Bild entfernen" onClick={() => removeImage(mediaObjectId)}>
                      <DeleteOutlineOutlined />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
            {mediaObjectIds.length < RELEASE_UPDATE_MAX_IMAGE_COUNT && (
              <Dropzone
                onChange={handleUpload}
                filesLimit={RELEASE_UPDATE_MAX_IMAGE_COUNT - mediaObjectIds.length}
                dropzoneText={isUploading ? "Bilder werden hochgeladen…" : "PNG oder JPG"}
                maxFileSize={RELEASE_UPDATE_MAX_IMAGE_SIZE}
                fileObjects={[]}
                acceptedFiles={["image/png", "image/jpeg"]}
                deletable={false}
                dropRejectMessage="Erlaubt sind PNG/JPEG mit maximal 15 MB pro Bild."
              />
            )}
          </Grid>
        </Grid>
      </ValidatorForm>
    </LegalbirdIoModal>
  );
}
